import { FormEvent } from 'react'
import { Box, Button, DialogActions, DialogContent, Stack, Typography } from '@mui/material'
import CustomDialog from '../../common/CustomDialog'
import DraggableDialogTitle from '../../common/DraggableDialogTitle'
import { ExtraSmallButton } from '../../custom'
import ClearIcon from '../../../assets/icons/ClearIcon'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../../assets/constants'
import { IndicatorSourceDependentDisplay } from '../../../util/indicators'

export type RemoveIndicatorDependentsDialogProps = {
    open: boolean
    dependents: IndicatorSourceDependentDisplay[]
    onClose: () => void
    onConfirm: () => void
}

const formatDependentLabel = ({ name, shortName }: IndicatorSourceDependentDisplay) =>
    shortName && name !== shortName ? `${name} (${shortName})` : name

const RemoveIndicatorDependentsDialog = ({
    open,
    dependents,
    onClose,
    onConfirm,
}: RemoveIndicatorDependentsDialogProps) => {
    const handleFormSubmit = (e: FormEvent) => {
        e.preventDefault()
        onConfirm()
    }

    return (
        <CustomDialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            container={document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID)}
            onEnterSubmit={onConfirm}
        >
            <Box component="form" onSubmit={handleFormSubmit} sx={{ display: 'contents' }}>
                <DraggableDialogTitle draggable={false}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                        <Typography variant="h6">Remove this indicator and its dependent indicators?</Typography>
                        <ExtraSmallButton onClick={onClose} aria-label="close" color="inherit">
                            <ClearIcon />
                        </ExtraSmallButton>
                    </Stack>
                </DraggableDialogTitle>
                <DialogContent sx={{ pt: 0, px: 2.5, pb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Doing this will remove this indicator and everything attached to it, including:
                    </Typography>
                    <Box
                        component="ul"
                        sx={{
                            m: 0,
                            pl: 2.5,
                            listStyleType: 'disc',
                            listStylePosition: 'outside',
                        }}
                    >
                        {dependents.map((dependent) => (
                            <Box
                                component="li"
                                key={dependent.seriesGroupId}
                                sx={{ display: 'list-item', mb: 0.5, pl: 0.5 }}
                            >
                                <Typography variant="body2" component="span">
                                    {formatDependentLabel(dependent)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2, pt: 1 }}>
                    <Button type="button" variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="error">
                        Remove
                    </Button>
                </DialogActions>
            </Box>
        </CustomDialog>
    )
}

export default RemoveIndicatorDependentsDialog
